import { useCallback, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import type {
  PipelineStageType,
  PipelineStageCandidateStatus,
} from '@/types/recruitment';

const MIN_STAGES = 3;

const STAGE_TYPES: { value: PipelineStageType; label: string; color: string }[] = [
  { value: 'SYSTEM', label: 'System', color: '#6B7280' },
  { value: 'OUTREACH', label: 'Outreach', color: '#3B82F6' },
  { value: 'INTERVIEW', label: 'Interview', color: '#8B5CF6' },
  { value: 'ASSESSMENT', label: 'Assessment', color: '#F97316' },
  { value: 'OFFER', label: 'Offer', color: '#10B981' },
  { value: 'CUSTOM', label: 'Custom', color: '#9CA3AF' },
];

const EDITABLE_TYPES = STAGE_TYPES.filter((t) => t.value !== 'SYSTEM');

const NONE_VALUE = '__none__';
const CANDIDATE_STATUS_OPTIONS: {
  value: PipelineStageCandidateStatus;
  label: string;
  color: string;
}[] = [
  { value: 'ACTIVE', label: 'Active', color: '#6B7280' },
  { value: 'HIRING', label: 'Hiring', color: '#D97706' },
  { value: 'HIRED', label: 'Hired', color: '#10B981' },
  { value: 'ARCHIVED', label: 'Archived', color: '#F59E0B' },
];

function typeMeta(type: PipelineStageType) {
  return STAGE_TYPES.find((t) => t.value === type) ?? STAGE_TYPES[STAGE_TYPES.length - 1];
}

function candidateStatusMeta(status: PipelineStageCandidateStatus | null) {
  if (!status) return null;
  return CANDIDATE_STATUS_OPTIONS.find((o) => o.value === status) ?? null;
}

export interface EditorStage {
  id: string;
  name: string;
  type: PipelineStageType;
  candidateStatus: PipelineStageCandidateStatus | null;
  position: number;
  isLocked: boolean;
}

export interface AddStageInput {
  name: string;
  type: PipelineStageType;
  candidateStatus: PipelineStageCandidateStatus | null;
}

export interface UpdateStageInput {
  name?: string;
  type?: PipelineStageType;
  candidateStatus?: PipelineStageCandidateStatus | null;
}

export interface PipelineStagesEditorProps {
  stages: EditorStage[];
  /** Optional per-stage active candidate count. Only shown when provided (vacancy context). */
  countByStageId?: Map<string, number>;
  onAdd: (input: AddStageInput) => void;
  onUpdate: (stageId: string, input: UpdateStageInput) => void;
  onDelete: (stageId: string) => void;
  onReorder: (positions: { id: string; position: number }[]) => void;
  pending?: { add?: boolean; update?: boolean };
}

export function PipelineStagesEditor({
  stages,
  countByStageId,
  onAdd,
  onUpdate,
  onDelete,
  onReorder,
  pending,
}: PipelineStagesEditorProps) {
  const sorted = [...stages].sort((a, b) => a.position - b.position);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    name: string;
    type: PipelineStageType;
    candidateStatus: PipelineStageCandidateStatus | null;
  }>({
    name: '',
    type: 'CUSTOM',
    candidateStatus: null,
  });
  const [isAdding, setIsAdding] = useState(false);
  const [addForm, setAddForm] = useState<{
    name: string;
    type: PipelineStageType;
    candidateStatus: PipelineStageCandidateStatus | null;
  }>({
    name: '',
    type: 'CUSTOM',
    candidateStatus: null,
  });
  const [deleteTarget, setDeleteTarget] = useState<EditorStage | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const editingStage = editingId ? sorted.find((s) => s.id === editingId) ?? null : null;
  const editingLocked = editingStage?.isLocked ?? false;

  const startEdit = (stage: EditorStage) => {
    setEditingId(stage.id);
    setEditForm({
      name: stage.name,
      type: stage.type,
      candidateStatus: stage.candidateStatus,
    });
  };

  const saveEdit = () => {
    if (!editingId) return;
    const trimmed = editForm.name.trim();
    if (!trimmed) return;
    const payload: UpdateStageInput = editingLocked
      ? { candidateStatus: editForm.candidateStatus }
      : {
          name: trimmed,
          type: editForm.type,
          candidateStatus: editForm.candidateStatus,
        };
    onUpdate(editingId, payload);
    setEditingId(null);
  };

  const handleAdd = () => {
    const trimmed = addForm.name.trim();
    if (!trimmed) return;
    onAdd({
      name: trimmed,
      type: addForm.type,
      candidateStatus: addForm.candidateStatus,
    });
    setAddForm({ name: '', type: 'CUSTOM', candidateStatus: null });
    setIsAdding(false);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    onDelete(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = sorted.findIndex((s) => s.id === active.id);
      const newIdx = sorted.findIndex((s) => s.id === over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      if (sorted[oldIdx].isLocked || sorted[newIdx].isLocked) return;
      const reordered = arrayMove(sorted, oldIdx, newIdx);
      onReorder(reordered.map((s, i) => ({ id: s.id, position: i + 1 })));
    },
    [sorted, onReorder],
  );

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-[13px] font-semibold text-foreground">Pipeline Stages</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Drag to reorder. Each stage has a type that categorizes its workflow role.
          </p>
        </div>
        <button
          type="button"
          className={cn(
            'flex h-9 w-9 items-center justify-center',
            'rounded-lg bg-hygge/10',
            'transition-colors',
            'hover:bg-hygge/20',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'cursor-pointer',
            isAdding && 'pointer-events-none opacity-50',
          )}
          onClick={() => setIsAdding(true)}
          disabled={isAdding}
          aria-label="Add stage"
        >
          <Plus className="h-4 w-4 text-hygge" />
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sorted.map((s) => s.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-0.5">
            {sorted.map((s) => (
              <div key={s.id}>
                <SortableStageRow
                  stage={s}
                  isEditing={editingId === s.id}
                  canDelete={!s.isLocked && sorted.length > MIN_STAGES}
                  count={countByStageId?.get(s.id)}
                  onStartEdit={startEdit}
                  onDelete={setDeleteTarget}
                  renderEditContent={
                    <div className="rounded-lg border border-hygge/20 bg-hygge/10 p-3">
                      {editingLocked ? (
                        <div className="mb-3 flex items-center gap-3">
                          <span
                            className="h-3.5 w-3.5 shrink-0 rounded-full"
                            style={{ background: typeMeta(editForm.type).color }}
                          />
                          <span
                            className="flex-1 truncate text-sm font-medium"
                            title={editForm.name}
                          >
                            {editForm.name}
                          </span>
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            System
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="mb-3 flex items-center gap-3">
                            <span
                              className="h-3.5 w-3.5 shrink-0 rounded-full"
                              style={{ background: typeMeta(editForm.type).color }}
                            />
                            <Input
                              data-slot="pipeline-stage-edit-input"
                              value={editForm.name}
                              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                              className="h-8 flex-1 text-sm outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none"
                              autoFocus
                              maxLength={100}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveEdit();
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                            />
                          </div>

                          <div className="mb-3 flex flex-wrap items-center gap-1.5">
                            {EDITABLE_TYPES.map((t) => {
                              const isActive = editForm.type === t.value;
                              return (
                                <button
                                  key={t.value}
                                  type="button"
                                  className={cn(
                                    'cursor-pointer rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors',
                                    isActive ? 'border-transparent text-white' : 'border-border hover:opacity-90',
                                  )}
                                  style={{
                                    background: isActive ? t.color : `${t.color}15`,
                                    color: isActive ? '#fff' : t.color,
                                  }}
                                  onClick={() => setEditForm((f) => ({ ...f, type: t.value }))}
                                >
                                  {t.label}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}

                      <div className="mb-3 flex items-center gap-3">
                        <label className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                          Status mapping
                        </label>
                        <Select
                          value={editForm.candidateStatus ?? NONE_VALUE}
                          onValueChange={(v) =>
                            setEditForm((f) => ({
                              ...f,
                              candidateStatus: v === NONE_VALUE ? null : (v as PipelineStageCandidateStatus),
                            }))
                          }
                        >
                          <SelectTrigger className="h-7 w-[140px] text-[11px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_VALUE}>None</SelectItem>
                            {CANDIDATE_STATUS_OPTIONS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <span className="text-[10px] text-muted-foreground">
                          Hired/Archived auto-update the candidate's result on entry.
                        </span>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 cursor-pointer border border-border text-xs"
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="h-7 cursor-pointer border-0 bg-cta text-xs font-semibold text-cta-foreground hover:bg-cta/90"
                          onClick={saveEdit}
                          disabled={pending?.update || !editForm.name.trim()}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  }
                />
              </div>
            ))}

            {isAdding && (
              <div className="mt-1 rounded-md border border-dashed border-border p-3 transition-colors hover:border-hygge/30 hover:bg-hygge/5">
                <div className="mb-3 flex items-center gap-3">
                  <span
                    className="h-3.5 w-3.5 shrink-0 rounded-full"
                    style={{ background: typeMeta(addForm.type).color }}
                  />
                  <Input
                    data-slot="pipeline-stage-edit-input"
                    value={addForm.name}
                    onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Stage name..."
                    className="h-8 flex-1 text-sm outline-none ring-0 focus:outline-none focus:ring-0 focus-visible:outline-none"
                    autoFocus
                    maxLength={100}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAdd();
                      if (e.key === 'Escape') setIsAdding(false);
                    }}
                  />
                </div>

                <div className="mb-3 flex flex-wrap items-center gap-1.5">
                  {EDITABLE_TYPES.map((t) => {
                    const isActive = addForm.type === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        className={cn(
                          'cursor-pointer rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors',
                          isActive ? 'border-transparent text-white' : 'border-border hover:opacity-90',
                        )}
                        style={{
                          background: isActive ? t.color : `${t.color}15`,
                          color: isActive ? '#fff' : t.color,
                        }}
                        onClick={() => setAddForm((f) => ({ ...f, type: t.value }))}
                      >
                        {t.label}
                      </button>
                    );
                  })}
                </div>

                <div className="mb-3 flex items-center gap-3">
                  <label className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Status mapping
                  </label>
                  <Select
                    value={addForm.candidateStatus ?? NONE_VALUE}
                    onValueChange={(v) =>
                      setAddForm((f) => ({
                        ...f,
                        candidateStatus: v === NONE_VALUE ? null : (v as PipelineStageCandidateStatus),
                      }))
                    }
                  >
                    <SelectTrigger className="h-7 w-[140px] text-[11px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE_VALUE}>None</SelectItem>
                      {CANDIDATE_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-[10px] text-muted-foreground">
                    Hired/Archived auto-update the candidate's result on entry.
                  </span>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 cursor-pointer border border-border text-xs"
                    onClick={() => setIsAdding(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 cursor-pointer border-0 bg-cta text-xs font-semibold text-cta-foreground hover:bg-cta/90"
                    onClick={handleAdd}
                    disabled={pending?.add || !addForm.name.trim()}
                  >
                    Add Stage
                  </Button>
                </div>
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => {
          if (!v) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="wrap-anywhere">
              Delete stage "{deleteTarget?.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this stage from the pipeline. Any candidates currently
              in this stage will need to be moved before you can delete it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Sortable Stage Row ──────────────────────────────────────────────────

interface SortableStageRowProps {
  stage: EditorStage;
  isEditing: boolean;
  canDelete: boolean;
  /** When undefined, the count badge is hidden (template editor has no per-stage candidate counts). */
  count: number | undefined;
  onStartEdit: (stage: EditorStage) => void;
  onDelete: (stage: EditorStage) => void;
  renderEditContent: React.ReactNode;
}

function SortableStageRow({
  stage,
  isEditing,
  canDelete,
  count,
  onStartEdit,
  onDelete,
  renderEditContent,
}: SortableStageRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
    disabled: isEditing || stage.isLocked,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
  };

  if (isEditing) {
    return <div>{renderEditContent}</div>;
  }

  const t = typeMeta(stage.type);
  const csMeta = candidateStatusMeta(stage.candidateStatus);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="group flex items-center gap-3 rounded-md px-3 py-2.5 transition-colors hover:bg-muted/80"
    >
      <div
        {...attributes}
        {...listeners}
        className={cn(
          'flex w-4 shrink-0 items-center justify-center opacity-40 transition-opacity touch-none',
          stage.isLocked
            ? 'cursor-not-allowed invisible'
            : 'cursor-grab group-hover:opacity-100 active:cursor-grabbing',
        )}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <span
        className="h-3.5 w-3.5 shrink-0 rounded-full"
        style={{ background: t.color }}
      />
      <span className="flex-1 truncate text-[13px] font-medium" title={stage.name}>
        {stage.name}
      </span>
      {csMeta && (
        <span
          className="rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: csMeta.color, background: `${csMeta.color}15` }}
          title="Candidates entering this stage are mapped to this lifecycle bucket"
        >
          → {csMeta.label}
        </span>
      )}
      <span
        className="rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
        style={{ color: t.color, background: `${t.color}15` }}
      >
        {t.label}
      </span>
      {count !== undefined && (
        <span className="min-w-[28px] text-right font-mono text-[11px] tabular-nums text-muted-foreground">
          {count}
        </span>
      )}
      <div className="flex gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        <button
          type="button"
          className="flex h-[26px] w-[26px] cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={() => onStartEdit(stage)}
          aria-label={stage.isLocked ? 'Edit status mapping' : 'Edit stage'}
          title={stage.isLocked ? 'Edit status mapping' : 'Edit stage'}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className={cn(
            'flex h-[26px] w-[26px] items-center justify-center rounded-md text-muted-foreground',
            stage.isLocked
              ? 'cursor-not-allowed invisible'
              : canDelete
                ? 'cursor-pointer hover:bg-muted hover:text-destructive'
                : 'cursor-not-allowed opacity-40',
          )}
          onClick={() => {
            if (!stage.isLocked && canDelete) onDelete(stage);
          }}
          disabled={stage.isLocked || !canDelete}
          aria-label="Delete stage"
          title={canDelete ? 'Delete stage' : `At least ${MIN_STAGES} stages required`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
